import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, FormControl } from '@angular/forms';
import { SharedModule, StringCustomFieldConfig, FormInputComponent, DynamicFormInputComponent, NotificationService, DataService } from '@vendure/admin-ui/core';
import { ID, Asset } from '@vendure/core';
import { Collection } from '../generated';
import { forkJoin, Observable, of } from 'rxjs';
import { safeJSONParse, areArraysEqual } from '../utils';
import { CollectionGroupData, defaultCollectionGroupData } from '../ui-types';
import { DEFAULT_ACTIVE_LANGUAGE } from '../constants';

@Component({
  selector: 'vdr-collection-group-input',
  template: `
    <vdr-card>
      <div>
        <!-- Group Name -->
        <vdr-form-field [label]="'Group Name'">
          <vdr-dynamic-form-input [control]="groupNameControl" [def]="textValueDef"></vdr-dynamic-form-input>
        </vdr-form-field>

        <!-- Description -->
        <vdr-form-field>
          <vdr-dynamic-form-input [control]="descriptionControl" [def]="textValueDef"></vdr-dynamic-form-input>
        </vdr-form-field>

        <!-- Background Asset -->
        <vdr-form-field [label]="'Background Asset'">
          <vdr-dynamic-form-input #backgroundAssetInput
            [control]="backgroundAssetControl"
            [def]="{
              type: 'relation',
              entity: 'Asset',
              ui: {
                component: 'relation-form-input'
              }
            }"
          >
          </vdr-dynamic-form-input>
        </vdr-form-field>

        <!-- Featured Assets -->
        <vdr-form-field [label]="'Featured Assets'">
          <vdr-dynamic-form-input #featuredAssetsInput
            [control]="featuredAssetsControl"
            [def]="{
              list: true,
              type: 'relation',
              entity: 'Asset',
              ui: {
                component: 'relation-form-input'
              }
            }"
          >
          </vdr-dynamic-form-input>
        </vdr-form-field>
      </div>

      <!-- Collection List -->
      <vdr-form-field [label]="'Collection List'">
        <vdr-dynamic-form-input #collectionListInput
          [control]="collectionListControl"
          [def]="{
            list: true,
            type: 'relation',
            entity: 'Collection',
            ui: {
              component: 'relation-collection-selector',
            }
          }"
        >
        </vdr-dynamic-form-input>
      </vdr-form-field>
    </vdr-card>
  `,
  standalone: true,
  imports: [SharedModule],
})
export class CollectionGroupInputComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit {
  @ViewChild('collectionListInput') collectionListInput: DynamicFormInputComponent;
  @ViewChild('featuredAssetsInput') featuredAssetsInput: DynamicFormInputComponent;
  @ViewChild('backgroundAssetInput') backgroundAssetInput: DynamicFormInputComponent;
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  activeLanguageCode = DEFAULT_ACTIVE_LANGUAGE;
  private languageChanges$?: Observable<unknown>;
  textValueDef: any = { type: 'string', ui: { component: 'text-value-input', args: { activeLanguageCode: this.activeLanguageCode } } };

  // Form controls
  collectionListControl = new UntypedFormControl();
  featuredAssetsControl = new UntypedFormControl();
  backgroundAssetControl = new UntypedFormControl();
  groupNameControl = new FormControl();
  descriptionControl = new FormControl();

  collectionGroupData: CollectionGroupData;

  constructor(
    private notificationService: NotificationService,
    private dataService: DataService
  ) {}

  ngOnInit() {
    const args = (this.config as any)?.ui?.args ?? {};
    if (typeof args.activeLanguageCode === 'string' && args.activeLanguageCode) {
      this.activeLanguageCode = args.activeLanguageCode;
    }
    if (args?.languageChanges$ && typeof args.languageChanges$.subscribe === 'function') {
      this.languageChanges$ = args.languageChanges$ as Observable<unknown>;
    }
    this.textValueDef = {
      ...this.textValueDef,
      ui: {
        ...this.textValueDef.ui,
        args: {
          activeLanguageCode: this.activeLanguageCode,
          languageChanges$: this.languageChanges$,
        },
      },
    };
    this.collectionGroupData = safeJSONParse<CollectionGroupData>(
      this.formControl.value,
      this.notificationService,
      defaultCollectionGroupData,
    )!;
    this.initializeFormControls();
    if (this.collectionGroupData?.backgroundAssetId) {
      this.dataService.product
        .getAsset(this.collectionGroupData.backgroundAssetId.toString())
        .mapSingle((data) => data.asset)
        .subscribe((asset) => {
          this.backgroundAssetControl.setValue(asset);
        });
    }
    if (this.collectionGroupData && this.collectionGroupData?.featuredAssetIds?.length > 0) {
      this.loadAssetsByIds(this.collectionGroupData.featuredAssetIds).subscribe((assets) => {
        this.featuredAssetsControl.setValue(assets);
        if (this.featuredAssetsInput) {
          this.featuredAssetsInput.writeValue(assets);
        }
      });
    } else {
      this.featuredAssetsControl.setValue([]);
    }
    if (this.collectionGroupData && this.collectionGroupData?.collectionIds?.length > 0) {
      this.loadCollectionsByIds(this.collectionGroupData.collectionIds).subscribe((collections) => {
        this.collectionListControl.setValue(collections);
        if (this.collectionListInput) {
          this.collectionListInput.writeValue(collections);
        }
      });
    } else {
      this.collectionListControl.setValue([]);
    }
  }

  ngAfterViewInit() {
    // Subscribe to value changes
    this.groupNameControl.valueChanges.subscribe(this.onGroupNameChange);
    this.descriptionControl.valueChanges.subscribe(this.onDescriptionChange);
    this.backgroundAssetControl.valueChanges.subscribe(this.onBackgroundAssetChange);

    // Handle featured assets and collections using DynamicFormInputComponent
    if (this.featuredAssetsInput) {
      this.featuredAssetsInput.registerOnChange(this.onFeaturedAssetsChange);
    }
    if (this.collectionListInput) {
      this.collectionListInput.registerOnChange(this.onCollectionsChange);
    }
  }

  // Initialize form controls with collectionGroupData values
  private initializeFormControls() {
    this.groupNameControl.setValue(this.toTextValueJson(this.collectionGroupData?.groupName));
    this.descriptionControl.setValue(this.toTextValueJson(this.collectionGroupData?.description));
  }

  private loadAssetsByIds(ids: ID[]): Observable<Asset[]> {
    if (!ids || ids.length === 0) {
      return of([]);
    }
    return forkJoin(ids.map((id) => this.dataService.product.getAsset(id.toString()).mapSingle((data) => data.asset))) as Observable<Asset[]>;
  }

  private loadCollectionsByIds(ids: ID[]): Observable<Collection[]> {
    if (!ids || ids.length === 0) {
      return of([]);
    }
    return forkJoin(ids.map((id) => this.dataService.collection.getCollections({
      filter: {
        id: { eq: id.toString() }
      },
    }).mapSingle((data) => data.collections?.items[0]))) as Observable<Collection[]>;
  }

  // Value change handlers for form controls
  private onGroupNameChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.collectionGroupData?.groupName) !== JSON.stringify(next)) {
      this.collectionGroupData!.groupName = next;
      this.updateJsonString();
    }
  }

  private onDescriptionChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.collectionGroupData?.description) !== JSON.stringify(next)) {
      this.collectionGroupData!.description = next;
      this.updateJsonString();
    }
  }

  private onBackgroundAssetChange = (value: Asset) => {
    const backgroundAssetId = value ? value.id : null;
    if (this.collectionGroupData?.backgroundAssetId !== backgroundAssetId) {
      this.collectionGroupData!.backgroundAssetId = backgroundAssetId;
      this.updateJsonString();
    }
  }

  private onFeaturedAssetsChange = (featuredAssets: Asset[]) => {
    const featuredAssetIds = featuredAssets.map((asset) => asset.id);
    if (!areArraysEqual(this.collectionGroupData?.featuredAssetIds||[], featuredAssetIds)) {
      this.collectionGroupData!.featuredAssetIds = featuredAssetIds;
      this.updateJsonString();
    }
  }

  private onCollectionsChange = (collections: Collection[]) => {
    const collectionIds = collections.map((collection) => collection.id);
    if (!areArraysEqual(this.collectionGroupData?.collectionIds||[], collectionIds)) {
      this.collectionGroupData!.collectionIds = collectionIds;
      this.updateJsonString();
    }
  };

  private toTextValueJson(raw: any): string {
    if (raw && typeof raw === 'object' && raw.i18n) return JSON.stringify(raw);
    return JSON.stringify({ format: 'plain', i18n: { [this.activeLanguageCode]: raw ?? '' } });
  }

  private parseTextValue(raw: string | null | undefined): any {
    if (!raw) return { format: 'plain', i18n: {} };
    try { return JSON.parse(raw); } catch { return { format: 'plain', i18n: {} }; }
  }

  // Update JSON string and form control value
  private updateJsonString() {
    const jsonString = JSON.stringify(this.collectionGroupData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}
